#!/usr/bin/env node

import { Command } from 'commander';
import { cosmiconfig } from 'cosmiconfig';
import pc from 'picocolors';
import { version } from '../package.json';
import { createInitCommand } from './commands/init';
import { createRunCommand } from './commands/run';
import { createSyncCommand } from './commands/sync';
import { createStartCommand } from './commands/start';
import { createStatusCommand } from './commands/status';
import { createKbCommand } from './commands/kb';
import { createConfigCommand } from './commands/config';
import { createTaskCommand } from './commands/task';
import { createWorkflowCommand } from './commands/workflow';
import { createTaskWorkflowCommand } from './commands/task-workflow';
import { createContextCommand } from './commands/context';
import { createAIContextCommand } from './commands/ai-context';
import { createClaudeHookCommand } from './commands/claude-hook';
import { createAutoDevCommand } from './commands/auto-dev';
import { createTemplateCommand } from './commands/template';
import { createTeamCommand } from './commands/team';
import { createNotifyCommand } from './commands/notify';
import { createOrchestrateCommand } from './commands/orchestrate';
import { createCheckPermissionsCommand } from './commands/check-permissions';
import { createParsePRDCommand } from './commands/parse-prd';
import { createNewCommand } from './commands/new';
import { createAgentCommand } from './commands/agent';
import { createThinkCommand } from './commands/think';
import { createPromptCommand } from './commands/prompt';
import { createComplexityCommand } from './commands/complexity';
import { createTagsCommand } from './commands/tags';
import { createConvertCommand } from './commands/convert';
import { createApiCommand } from './commands/api';
import { createUICommand } from './commands/ui';
import { createMcpCommand } from './commands/mcp';
import { createStatsCommand } from './commands/stats';
import { createDocCommand } from './commands/doc';
import { createProjectCommand } from './commands/project';
import { createSyncStatusCommand } from './commands/sync-status';
import { createClaudeCommand } from './commands/claude';
import { createDesignCommand } from './commands/design';
import { createHooksCommand } from './commands/hooks';
import { createEarsCommand } from './commands/ears';
import { createTriggersCommand } from './commands/triggers';
import { createCollaborateCommand } from './commands/collaborate';
import { createVisualizeCommand } from './commands/visualize';
import { createMigrateCommand } from './commands/migrate';
import { createAICommand } from './commands/ai';
import { createWorkflowV2Command } from './commands/workflow-v2';
import { createWorkflowMonitorCommand } from './commands/workflow-monitor';
import { createWorkflowTemplateCommand } from './commands/workflow-template';
import { createPerformanceCommand } from './commands/performance';
import { createTestCommand } from './commands/test';
import { 
  createNextCommand, 
  createDoneCommand, 
  createNowCommand, 
  createTodayCommand, 
  createQuickCommand 
} from './commands/quick-commands';
import { logger } from './utils/logger';

async function main() {
  const program = new Command();

  // Load configuration
  const explorer = cosmiconfig('workflow');
  const config = await explorer.search();

  program
    .name('workflow')
    .description('Command-line tool for fast task management and API services (pair with Web UI for full features)')
    .version(version, '-v, --version', 'Display version number')
    .alias('wf')
    .option('-d, --debug', 'Enable debug mode')
    .option('--no-webhook', 'Disable webhook notifications')
    .option('--webhook-url <url>', 'Set custom webhook URL')
    .hook('preAction', (thisCommand) => {
      if (thisCommand.opts().debug) {
        process.env.DEBUG = 'true';
        logger.debug('Debug mode enabled');
      }
      
      // Handle webhook options
      if (thisCommand.opts().webhook === false) {
        process.env.WORKFLOW_WEBHOOK_ENABLED = 'false';
        logger.debug('Webhook notifications disabled');
      }
      
      if (thisCommand.opts().webhookUrl) {
        process.env.WORKFLOW_WEBHOOK_URL = thisCommand.opts().webhookUrl;
        logger.debug(`Webhook URL set to: ${thisCommand.opts().webhookUrl}`);
      }
    });

  // Add commands
  program.addCommand(createInitCommand());
  program.addCommand(createRunCommand());
  program.addCommand(createSyncCommand());
  program.addCommand(createStartCommand());
  program.addCommand(createStatusCommand());
  program.addCommand(createKbCommand());
  program.addCommand(createConfigCommand());
  program.addCommand(createTaskCommand());
  
  // New workflow commands
  program.addCommand(createWorkflowCommand());
  program.addCommand(createTaskWorkflowCommand());
  program.addCommand(createContextCommand());
  program.addCommand(createAIContextCommand());
  program.addCommand(createClaudeHookCommand());
  program.addCommand(createAutoDevCommand());
  program.addCommand(createTemplateCommand());
  program.addCommand(createTeamCommand());
  program.addCommand(createNotifyCommand());
  program.addCommand(createOrchestrateCommand());
  program.addCommand(createCheckPermissionsCommand());
  program.addCommand(createNewCommand());
  program.addCommand(createAgentCommand());
  program.addCommand(createThinkCommand());
  program.addCommand(createPromptCommand());
  program.addCommand(createParsePRDCommand());
  program.addCommand(createComplexityCommand());
  program.addCommand(createTagsCommand());
  program.addCommand(createConvertCommand());
  program.addCommand(createApiCommand());
  program.addCommand(createUICommand());
  program.addCommand(createMcpCommand());
  program.addCommand(createStatsCommand());
  program.addCommand(createDocCommand());
  program.addCommand(createProjectCommand());
  program.addCommand(createSyncStatusCommand());
  program.addCommand(createClaudeCommand());
  program.addCommand(createDesignCommand());
  program.addCommand(createHooksCommand());
  program.addCommand(createEarsCommand());
  program.addCommand(createTriggersCommand());
  program.addCommand(createCollaborateCommand());
  program.addCommand(createVisualizeCommand());
  program.addCommand(createMigrateCommand());
  program.addCommand(createAICommand());
  program.addCommand(createWorkflowV2Command());
  program.addCommand(createWorkflowMonitorCommand());
  program.addCommand(createWorkflowTemplateCommand(process.cwd()));
  program.addCommand(createPerformanceCommand());
  program.addCommand(createTestCommand());
  
  // Quick commands
  program.addCommand(createNextCommand());
  program.addCommand(createDoneCommand());
  program.addCommand(createNowCommand());
  program.addCommand(createTodayCommand());
  program.addCommand(createQuickCommand());

  // Error handling
  program.exitOverride();

  try {
    await program.parseAsync(process.argv);
  } catch (error: any) {
    if (error.code === 'commander.helpDisplayed') {
      process.exit(0);
    }
    logger.error(error.message);
    if (process.env.DEBUG) {
      console.error(error);
    }
    process.exit(1);
  }
}

main().catch((error) => {
  logger.error('Fatal error:', error);
  process.exit(1);
});